import { useState, useEffect, useRef } from "react";
import { type Address } from "viem";
import { X, Search, ChevronDown } from "lucide-react";
import { type TokenInfo, NATIVE_JASMY } from "../../config/tokens";
import { useTokenBalance } from "../../hooks/useTokenBalance";
import { useTokenList } from "../../hooks/useTokenList";
import { formatTokenAmount, isValidAddress, cn } from "../../lib/utils";
import { useTokenInfo } from "../../hooks/useTokenBalance";

function TokenLogo({ token, size = "md" }: { token: TokenInfo; size?: "sm" | "md" }) {
  const [failed, setFailed] = useState(false);
  const dim = size === "sm" ? "w-5 h-5 text-[8px]" : "w-8 h-8 text-xs";
  if (token.logoUrl && !failed) {
    return (
      <img
        src={token.logoUrl}
        alt={token.symbol}
        className={`${dim} rounded-full object-cover`}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className={`${dim} rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary`}>
      {token.symbol.slice(0, 2)}
    </div>
  );
}

interface TokenSelectorProps {
  selectedToken: TokenInfo;
  onSelect: (token: TokenInfo) => void;
  otherToken?: TokenInfo;
}

function TokenBalance({ token }: { token: TokenInfo }) {
  const { balance, decimals } = useTokenBalance(token.isNative ? undefined : token.address);
  return (
    <span className="text-xs text-text-tertiary">
      {formatTokenAmount(balance, decimals)}
    </span>
  );
}

function CustomTokenEntry({
  address,
  onSelect,
}: {
  address: Address;
  onSelect: (token: TokenInfo) => void;
}) {
  const { name, symbol, decimals, isLoading } = useTokenInfo(address);

  if (isLoading || !symbol) return null;

  const token: TokenInfo = {
    address,
    symbol,
    name,
    decimals,
    logoUrl: "",
  };

  return (
    <button
      onClick={() => onSelect(token)}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-hover transition-colors rounded-lg"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-xs font-bold text-secondary">
          {symbol.slice(0, 2)}
        </div>
        <div className="text-left">
          <div className="text-sm font-medium text-text-primary">{symbol}</div>
          <div className="text-xs text-text-tertiary">{name}</div>
        </div>
      </div>
      <span className="text-xs text-primary">Import</span>
    </button>
  );
}

export function TokenSelector({ selectedToken, onSelect, otherToken }: TokenSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { tokens: tokenList, addToken } = useTokenList();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const filteredTokens = tokenList.filter((t) => {
    if (
      otherToken &&
      t.address === otherToken.address &&
      t.isNative === otherToken.isNative
    ) {
      return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.symbol.toLowerCase().includes(q) ||
      t.name.toLowerCase().includes(q) ||
      t.address.toLowerCase().includes(q)
    );
  });

  const isCustomAddress = search && isValidAddress(search) && filteredTokens.length === 0;

  function handleSelect(token: TokenInfo) {
    addToken(token);
    onSelect(token);
    setIsOpen(false);
    setSearch("");
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 bg-surface-hover border border-border rounded-button px-3 py-2 hover:border-border-bright transition-all shrink-0"
      >
        <TokenLogo token={selectedToken} size="sm" />
        <span className="font-semibold text-sm text-text-primary">
          {selectedToken.symbol}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-text-tertiary" />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm animate-fade-in">
          <div
            ref={modalRef}
            className="w-full max-w-md glass-card p-0 overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-display font-semibold text-lg">Select Token</h3>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearch("");
                }}
                className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors"
              >
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or paste address"
                  className="input-field pl-10"
                />
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto p-2">
              {filteredTokens.map((token) => (
                <button
                  key={`${token.address}-${token.isNative}`}
                  onClick={() => handleSelect(token)}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-3 hover:bg-surface-hover transition-colors rounded-lg",
                    selectedToken.address === token.address &&
                      selectedToken.isNative === token.isNative &&
                      "bg-primary/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <TokenLogo token={token} size="md" />
                    <div className="text-left">
                      <div className="text-sm font-medium text-text-primary">
                        {token.symbol}
                      </div>
                      <div className="text-xs text-text-tertiary">{token.name}</div>
                    </div>
                  </div>
                  <TokenBalance token={token} />
                </button>
              ))}

              {isCustomAddress && (
                <CustomTokenEntry
                  address={search as Address}
                  onSelect={handleSelect}
                />
              )}

              {filteredTokens.length === 0 && !isCustomAddress && (
                <div className="text-center py-8 text-text-tertiary text-sm">
                  No tokens found
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
